import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@/components/synapse/Icon';
import { Stepper } from '@/components/synapse/Stepper';
import { Field } from '@/components/synapse/Field';
import { Select } from '@/components/synapse/Select';
import { NotesTextarea } from '@/components/synapse/NotesTextarea';
import { Segmented } from '@/components/synapse/Segmented';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { PatientSearchField } from '@/features/patients/PatientSearchField';
import { PatientList } from '@/features/patients/PatientList';
import { useResultNavigation } from '@/features/patients/useResultNavigation';
import { QuickCreatePatient } from '@/features/patients/QuickCreatePatient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePatient } from '@/features/patients/usePatients';
import { ageFromDob, sexLabelKey } from '@/features/patients/patientDisplay';
import { useClinicMembers } from '@/hooks/usePeople';
import { useAuth } from '@/hooks/useAuth';
import { useCan } from '@/hooks/usePermissions';
import {
  useCreateLabOrder,
  useLabCatalogue,
  useLabLaboratories,
} from '@/features/lab/useLab';
import {
  addFreeTextTest,
  buildCreateInput,
  emptyDraft,
  firstInvalidStep,
  NEW_ORDER_STEPS,
  reconcileSpecimens,
  summariseSpecimens,
  toggleTest,
  validateStep,
  type NewOrderDraft,
  type NewOrderStep,
  type SelectedTest,
  type SpecimenDraft,
} from '@/features/lab/newOrder';
import { translateSpecimenType } from '@/lib/labTests';
import { translateApiError } from '@/lib/apiError';
import { MAX_TESTS_PER_ORDER, SPECIMEN_TYPES, type SpecimenType } from '@synapse/lab-tests';
import type { LabTestDefinitionDTO } from '@/types/lab';
import type { Patient } from '@/types/patients';

/**
 * Raise a lab order: patient, tests, then what was actually drawn.
 *
 * The middle step is the one that earns the flow its keep. Choosing tests
 * DERIVES the specimens — an FBC and an HbA1c are one EDTA tube, a UFR is a
 * separate pot — so step three is a short list of cards to confirm rather than a
 * form asking which tubes to fill. There is deliberately no way to add a
 * specimen by hand: one for a test nobody ordered is refused by the API, and the
 * better answer is to make it unrepresentable.
 *
 * Saving with nothing collected is legal and lands as `awaiting_collection`. The
 * patient sent away to come back fasting still needs their requisition today.
 */
export function NewLabOrderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();

  const [stepIndex, setStepIndex] = useState(0);
  const step: NewOrderStep = NEW_ORDER_STEPS[stepIndex] ?? 'patient';

  // A patient id in the URL is how the patient record's "New lab order" button
  // arrives here — the patient is then fixed, not re-searched.
  const lockedPatientId = params.get('patientId');
  const [draft, setDraft] = useState<NewOrderDraft>(() => ({
    ...emptyDraft(user?.id ?? null),
    patientId: lockedPatientId,
  }));

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const create = useCreateLabOrder();

  const stepError = validateStep(step, draft);

  function patchDraft(patch: Partial<NewOrderDraft>) {
    setDraft((d) => {
      const next = { ...d, ...patch };
      // Specimens follow the tests, always — recomputed here rather than in an
      // effect so the cards can never render a frame out of step with the
      // selection that produced them.
      if (patch.tests) next.specimens = reconcileSpecimens(next.tests, d.specimens);
      return next;
    });
  }

  function goNext() {
    if (stepError) {
      setTouched(true);
      return;
    }
    setTouched(false);
    setStepIndex((i) => Math.min(i + 1, NEW_ORDER_STEPS.length - 1));
  }

  function goBack() {
    setTouched(false);
    if (stepIndex === 0) navigate('/lab');
    else setStepIndex((i) => i - 1);
  }

  async function submit() {
    // The WHOLE draft, not just the step on screen. `buildCreateInput` throws a
    // raw untranslated Error if the patient is missing, and that string would go
    // straight to the operator — so the last thing between them and the request
    // has to be the same validation the steps use.
    const invalid = firstInvalidStep(draft);
    if (invalid) {
      setTouched(true);
      setStepIndex(NEW_ORDER_STEPS.indexOf(invalid.step));
      return;
    }
    setSubmitError(null);
    try {
      const order = await create.mutateAsync(buildCreateInput(draft));
      // Straight to the order, where the accession is displayed large enough to
      // copy onto a tube. A separate success screen would be one more click
      // between the operator and the number they came for.
      navigate(`/lab/${order.id}`);
    } catch (err) {
      setSubmitError(translateApiError(err, { fallback: t('lab.new.submitFailed') }));
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 px-4 py-6">
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/lab')}
            aria-label={t('lab.detail.back')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-paper-2 hover:text-ink"
          >
            <Icon name="chevron_left" size={16} />
          </button>
          <h1 className="text-xl font-semibold tracking-[-0.02em] text-ink">
            {t('lab.new.title')}
          </h1>
        </div>
        <Stepper
          step={stepIndex + 1}
          labels={[t('lab.new.stepPatient'), t('lab.new.stepTests'), t('lab.new.stepCollection')]}
        />
      </header>

      {step === 'patient' && (
        <PatientStep
          patientId={draft.patientId}
          locked={Boolean(lockedPatientId)}
          onChange={(patientId) => patchDraft({ patientId })}
        />
      )}

      {step === 'tests' && (
        <TestsStep tests={draft.tests} onChange={(tests) => patchDraft({ tests })} />
      )}

      {step === 'collection' && (
        <CollectionStep draft={draft} onChange={patchDraft} />
      )}

      {touched && stepError && (
        <p role="alert" className="text-[13px] text-destructive">
          {t(stepError)}
        </p>
      )}
      {submitError && (
        <p role="alert" className="text-[13px] text-destructive">
          {submitError}
        </p>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-line-2 pt-4">
        <Button variant="secondary" onClick={goBack} disabled={create.isPending}>
          {stepIndex === 0 ? t('common.cancel') : t('lab.new.back')}
        </Button>

        {step === 'collection' ? (
          <Button onClick={() => void submit()} disabled={create.isPending}>
            {create.isPending ? t('lab.new.saving') : t('lab.new.submit')}
          </Button>
        ) : (
          // Not disabled on an invalid step: a disabled button gives no reason.
          // Pressing it reveals what is missing.
          <Button onClick={goNext}>{t('lab.new.next')}</Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — patient
// ---------------------------------------------------------------------------

function PatientStep({
  patientId,
  locked,
  onChange,
}: {
  patientId: string | null;
  locked: boolean;
  onChange: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const can = useCan();
  const [query, setQuery] = useState('');
  const [registering, setRegistering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null!);

  const chosen = usePatient(patientId, { enabled: Boolean(patientId) });

  // ↑/↓/Enter over the results, and the `aria-activedescendant` that tells a
  // screen reader which one is current. `PatientSearchField` hardcodes
  // `role="combobox"` and `aria-controls="patient-results"`, so the list it
  // names has to be the real `PatientList` — rendering a bare `<ul>` next to it
  // leaves `aria-controls` pointing at nothing and the keyboard dead, on the one
  // screen where the operator is holding a tube in the other hand.
  const { highlightedId, activeDescendantId, onKeyDown, setPatients } = useResultNavigation(
    (p) => onChange(p.id),
  );

  if (patientId) {
    // `?patientId=` is taken on trust, so a stale or mistyped link renders this
    // branch for a patient that does not resolve. `locked` must not suppress the
    // way out in that case, or the operator is left on a step that will never
    // validate, with no control that changes anything.
    const lockedButBroken = locked && chosen.isError;

    return (
      <section className="flex flex-col gap-3">
        <h2 className="text-[13px] font-medium text-muted">{t('lab.new.patientChosen')}</h2>
        {chosen.isPending && (
          <div className="h-[68px] animate-pulse rounded-md border border-line-2 bg-paper-2" />
        )}
        {chosen.data && <ChosenPatientCard patient={chosen.data} />}
        {chosen.isError && (
          <p className="text-[13px] text-destructive">{t('lab.new.patientLoadFailed')}</p>
        )}
        {(!locked || lockedButBroken) && (
          <Button variant="secondary" className="self-start" onClick={() => onChange(null)}>
            {lockedButBroken ? t('lab.new.choosePatient') : t('lab.new.changePatient')}
          </Button>
        )}
      </section>
    );
  }

  // A walk-in sent straight to the lab has no record yet, and making the
  // operator leave the flow to create one loses everything they have entered.
  if (registering) {
    return (
      <QuickCreatePatient
        initialName={query.trim()}
        onCreated={(p) => {
          setRegistering(false);
          onChange(p.id);
        }}
        onCancel={() => setRegistering(false)}
      />
    );
  }

  return (
    // Input and list share one keydown wrapper so arrows typed in the focused
    // input reach the handler that moves the highlight — the same arrangement
    // both other consumers of this field use.
    <section className="flex flex-col gap-3" onKeyDown={onKeyDown}>
      <PatientSearchField
        value={query}
        onChange={setQuery}
        inputRef={inputRef}
        autoFocus
        activeDescendantId={activeDescendantId}
      />

      {can('patients.create') && (
        <Button
          variant="secondary"
          className="self-start gap-1.5"
          onClick={() => setRegistering(true)}
        >
          <Icon name="plus" size={14} />
          {t('lab.new.registerPatient')}
        </Button>
      )}

      <PatientList
        query={query}
        onSelect={(p) => onChange(p.id)}
        onAddNew={() => setRegistering(true)}
        variant="card"
        highlightedId={highlightedId}
        onResultsChange={setPatients}
      />
    </section>
  );
}

/**
 * The chosen patient, shown large.
 *
 * This is a safety control, not decoration: the operator is about to label a
 * tube, and the name and photo in front of them is what catches an order raised
 * against the wrong record before a needle goes anywhere.
 */
function ChosenPatientCard({ patient }: { patient: Patient }) {
  const { t } = useTranslation();
  const age = ageFromDob(patient.dateOfBirth);
  return (
    <div className="flex items-center gap-3.5 rounded-md border border-line-2 bg-paper px-3.5 py-3">
      <InitialsAvatar name={patient.fullName} size={44} photoUrl={patient.photoUrl} />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-[15px] font-semibold tracking-[-0.01em] text-ink">
          {patient.fullName}
        </span>
        <span className="text-[12.5px] text-muted">
          {[
            patient.mrn,
            age != null ? `${age} ${t('patients.yrs')}` : null,
            patient.sex ? t(sexLabelKey(patient.sex)) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — tests
// ---------------------------------------------------------------------------

function TestsStep({
  tests,
  onChange,
}: {
  tests: SelectedTest[];
  onChange: (tests: SelectedTest[]) => void;
}) {
  const { t } = useTranslation();
  const catalogue = useLabCatalogue();
  const [filter, setFilter] = useState('');
  const [freeText, setFreeText] = useState('');
  const [freeTextType, setFreeTextType] = useState<SpecimenType>('blood');
  const [rejection, setRejection] = useState<string | null>(null);

  const selectedCodes = useMemo(() => new Set(tests.map((x) => x.code)), [tests]);
  const full = tests.length >= MAX_TESTS_PER_ORDER;

  const grouped = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const active = (catalogue.data?.tests ?? []).filter(
      (d) =>
        d.active &&
        (needle === '' ||
          d.name.toLowerCase().includes(needle) ||
          d.code.toLowerCase().includes(needle)),
    );
    return SPECIMEN_TYPES.map((type) => ({
      type,
      tests: active.filter((d) => d.specimenType === type),
    })).filter((g) => g.tests.length > 0);
  }, [catalogue.data, filter]);

  function addTyped() {
    const result = addFreeTextTest(tests, freeText, freeTextType);
    if (result.rejection) {
      setRejection(`lab.new.errors.freeText.${result.rejection}`);
      return;
    }
    setRejection(null);
    setFreeText('');
    onChange(result.tests);
  }

  const summary = summariseSpecimens(tests);

  return (
    <section className="flex flex-col gap-4">
      {/*
        The summary sits ABOVE the picker, not below it. It is the answer to
        "which tubes do I need?", and putting it under a long catalogue would
        mean scrolling past everything to find it.
      */}
      {summary.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-line-2 bg-paper-2 px-3.5 py-3">
          <p className="text-[12px] font-medium text-muted">{t('lab.new.willDraw')}</p>
          <ul className="flex flex-wrap gap-2">
            {summary.map((s) => (
              <li
                key={s.specimenType}
                className="rounded-full border border-line-2 bg-paper px-3 py-1 text-[12.5px] text-ink"
              >
                {t('lab.new.drawLine', {
                  type: translateSpecimenType(t, s.specimenType),
                  container: s.container ?? t('lab.new.anyContainer'),
                  count: s.testCount,
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-medium text-muted">{t('lab.new.filterTests')}</span>
        <span className="flex h-11 items-center gap-2 rounded-md border border-line-2 bg-paper px-3">
          <Icon name="search" size={15} className="shrink-0 text-muted" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label={t('lab.new.filterTests')}
            placeholder={t('lab.new.filterTestsPlaceholder')}
            className="h-full flex-1 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-muted-2"
          />
        </span>
      </label>

      {catalogue.isPending && (
        <div className="h-24 animate-pulse rounded-md border border-line-2 bg-paper-2" />
      )}
      {catalogue.isError && (
        <div className="rounded-md border border-line-2 bg-paper px-4 py-5 text-center">
          <p className="text-[13.5px] text-ink">{t('lab.new.catalogueFailed')}</p>
          <Button variant="secondary" className="mt-3" onClick={() => void catalogue.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      )}

      {grouped.map((group) => (
        <div key={group.type} className="flex flex-col gap-2">
          <h3 className="text-[12px] font-medium uppercase tracking-[0.04em] text-muted">
            {translateSpecimenType(t, group.type)}
          </h3>
          <div className="flex flex-wrap gap-2">
            {group.tests.map((def) => (
              <TestChip
                key={def.code}
                def={def}
                selected={selectedCodes.has(def.code)}
                // A full order still lets you deselect — otherwise the operator
                // is stuck at the cap with no way to swap one test for another.
                disabled={full && !selectedCodes.has(def.code)}
                onToggle={() => onChange(toggleTest(tests, def))}
              />
            ))}
          </div>
        </div>
      ))}

      {!catalogue.isPending && grouped.length === 0 && filter.trim() !== '' && (
        <p className="text-[13px] text-muted">{t('lab.new.noTestsMatch')}</p>
      )}

      <div className="flex flex-col gap-2 border-t border-line-2 pt-4">
        <p className="text-[12px] font-medium text-muted">{t('lab.new.otherTest')}</p>
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t('lab.new.otherTestName')} className="min-w-[200px] flex-1">
            {(id) => (
              <Input
                id={id}
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder={t('lab.new.otherTestPlaceholder')}
                list="lab-past-free-text"
              />
            )}
          </Field>
          {/*
            Typed tests must name their own specimen type — there is no
            catalogue entry to infer it from, and the collection step cannot
            derive a tube for a test that does not say what it needs.
          */}
          <Field label={t('lab.new.otherTestType')} className="w-40">
            {(id) => (
              <Select
                id={id}
                value={freeTextType}
                onChange={(e) => setFreeTextType(e.target.value as SpecimenType)}
              >
                {SPECIMEN_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {translateSpecimenType(t, type)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button variant="secondary" onClick={addTyped} disabled={full} className="h-10">
            {t('common.add')}
          </Button>
        </div>

        {/* Names this clinic has typed before, offered without imposing them. */}
        <datalist id="lab-past-free-text">
          {(catalogue.data?.pastFreeTextNames ?? []).map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>

        {rejection && (
          <p role="alert" className="text-[12.5px] text-destructive">
            {t(rejection, { max: MAX_TESTS_PER_ORDER })}
          </p>
        )}
      </div>

      {tests.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-line-2 pt-4">
          <p className="text-[12px] font-medium text-muted">
            {t('lab.new.chosenCount', { count: tests.length })}
          </p>
          <ul className="flex flex-wrap gap-2">
            {tests.map((test) => (
              <li key={test.code}>
                <button
                  type="button"
                  onClick={() => onChange(tests.filter((x) => x.code !== test.code))}
                  aria-label={t('lab.new.removeTest', { test: test.name })}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-ink bg-ink px-3 text-[12.5px] text-accent-foreground"
                >
                  {test.name}
                  <Icon name="x" size={12} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function TestChip({
  def,
  selected,
  disabled,
  onToggle,
}: {
  def: LabTestDefinitionDTO;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onToggle}
      className={
        'inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3.5 text-[13.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 ' +
        (selected
          ? 'border border-ink bg-ink text-accent-foreground'
          : 'border border-line-2 bg-paper text-ink-2 hover:border-line')
      }
    >
      {selected && <Icon name="check" size={13} strokeWidth={2.4} />}
      {def.name}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — collection
// ---------------------------------------------------------------------------

function CollectionStep({
  draft,
  onChange,
}: {
  draft: NewOrderDraft;
  onChange: (patch: Partial<NewOrderDraft>) => void;
}) {
  const { t } = useTranslation();
  const can = useCan();
  const { user } = useAuth();

  // A lab tech may hold `lab.orders.create` without `members.read`, so the
  // member list is asked for only when it would be allowed. Without it the
  // orderer is still expressible: yourself, or a name typed in.
  const canListMembers = can('members.read');
  const members = useClinicMembers({ enabled: canListMembers });

  // No `includeInactive`: ordering must never offer a lab the clinic retired.
  const laboratories = useLabLaboratories();

  const specimenTypes = Object.keys(draft.specimens) as SpecimenType[];

  function patchSpecimen(type: SpecimenType, patch: Partial<SpecimenDraft>) {
    const current = draft.specimens[type];
    if (!current) return;
    onChange({ specimens: { ...draft.specimens, [type]: { ...current, ...patch } } });
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h2 className="text-[13px] font-medium text-muted">{t('lab.new.samples')}</h2>
        {specimenTypes.map((type) => {
          const spec = draft.specimens[type];
          if (!spec) return null;
          return (
            <SpecimenCard
              key={type}
              type={type}
              spec={spec}
              onPatch={(patch) => patchSpecimen(type, patch)}
            />
          );
        })}
        <p className="text-[12px] text-muted">{t('lab.new.uncollectedHint')}</p>
      </div>

      <div className="flex flex-col gap-3 border-t border-line-2 pt-4">
        <Field label={t('lab.orderedBy')}>
          {(id) => (
            <div className="flex flex-col gap-2">
              <Select
                id={id}
                value={
                  draft.ordering.kind === 'external' ? '__external__' : draft.ordering.userId
                }
                onChange={(e) =>
                  onChange({
                    ordering:
                      e.target.value === '__external__'
                        ? { kind: 'external', label: '' }
                        : { kind: 'member', userId: e.target.value },
                  })
                }
              >
                {user && (
                  <option value={user.id}>{t('lab.new.orderedByMe', { name: user.name })}</option>
                )}
                {(members.data ?? [])
                  .filter((m) => m.id !== user?.id)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                <option value="__external__">{t('lab.new.orderedByExternal')}</option>
              </Select>

              {draft.ordering.kind === 'external' && (
                <Input
                  value={draft.ordering.label}
                  onChange={(e) => onChange({ ordering: { kind: 'external', label: e.target.value } })}
                  aria-label={t('lab.new.externalName')}
                  placeholder={t('lab.new.externalPlaceholder')}
                />
              )}
            </div>
          )}
        </Field>

        <Field label={t('lab.new.priority')}>
          {() => (
            <Segmented
              value={draft.priority}
              onChange={(priority) => onChange({ priority })}
              options={[
                { value: 'routine' as const, labelKey: 'lab.priority.routine' },
                { value: 'urgent' as const, labelKey: 'lab.priority.urgent' },
              ]}
              ariaLabel={t('lab.new.priority')}
            />
          )}
        </Field>

        {/*
          The laboratory belongs on this step, not the tests step: steps 1–2 are
          "who" and "what", and this one is the requisition's logistics — who
          ordered it, how urgent, what was drawn. Routing is a dispatch fact of
          that same kind, and putting it beside the tests would imply per-test
          routing the schema deliberately does not model.

          A clinic with no laboratories sees no control at all rather than a dead
          dropdown. Active labs only — a retired one must never be pickable, and
          the server refuses it anyway.
        */}
        {laboratories.data && laboratories.data.length > 0 && (
          <Field
            label={t('lab.laboratory')}
            hint={t('lab.new.laboratoryHint')}
            optional
          >
            {(id) => (
              <Select
                id={id}
                value={draft.laboratoryId ?? ''}
                onChange={(e) => onChange({ laboratoryId: e.target.value || null })}
              >
                <option value="">{t('lab.laboratoryUnset')}</option>
                {laboratories.data.map((lab) => (
                  <option key={lab.id} value={lab.id}>
                    {lab.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <Field label={t('lab.detail.indication')} optional>
          {(id) => (
            <NotesTextarea
              id={id}
              value={draft.indication}
              onChange={(indication) => onChange({ indication })}
              placeholderKey="lab.new.indicationPlaceholder"
              minRows={2}
            />
          )}
        </Field>
      </div>
    </section>
  );
}

function SpecimenCard({
  type,
  spec,
  onPatch,
}: {
  type: SpecimenType;
  spec: SpecimenDraft;
  onPatch: (patch: Partial<SpecimenDraft>) => void;
}) {
  const { t } = useTranslation();
  const label = translateSpecimenType(t, type);

  return (
    <div className="flex flex-col gap-3 rounded-md border border-line-2 bg-paper px-3.5 py-3">
      <label className="flex min-h-[44px] items-center gap-2.5">
        <input
          type="checkbox"
          checked={spec.collect}
          onChange={(e) => onPatch({ collect: e.target.checked })}
          className="h-4 w-4"
        />
        <span className="text-[13.5px] font-semibold text-ink">
          {t('lab.new.collectedType', { type: label })}
        </span>
      </label>

      {spec.collect && (
        <div className="flex flex-wrap gap-3">
          <Field label={t('lab.new.collectedAt')} className="min-w-[190px] flex-1">
            {(id) => (
              <Input
                id={id}
                type="datetime-local"
                value={spec.collectedAt}
                onChange={(e) => onPatch({ collectedAt: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('lab.new.container')} className="min-w-[150px] flex-1" optional>
            {(id) => (
              <Input
                id={id}
                value={spec.container}
                onChange={(e) => onPatch({ container: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('lab.new.labelNote')} className="min-w-[150px] flex-1" optional>
            {(id) => (
              <Input
                id={id}
                value={spec.labelNote}
                onChange={(e) => onPatch({ labelNote: e.target.value })}
              />
            )}
          </Field>
        </div>
      )}
    </div>
  );
}
